import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useEffect, useRef, useState } from "react";
import { Avatar, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatPhone } from "../../lib/format";
import { useContacts, useCreateCase } from "../../lib/queries";
import { type CaseDraft, CaseFields, EMPTY_CASE } from "./CaseFields";
import { caseNumber } from "./case-text";

type Picked = { id: string; name: string | null; waId: string };

/**
 * Opens a case. From a conversation the customer is known and the team stays in the chat;
 * from the list, the customer is picked and the new case opens.
 */
export function CaseDialog({
  contact,
  conversationId,
  onClose,
}: {
  contact?: Picked;
  conversationId?: string;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const create = useCreateCase();
  const toast = useToast();
  const navigate = useNavigate();
  const [who, setWho] = useState<Picked | null>(contact ?? null);
  const [d, setD] = useState<CaseDraft>(EMPTY_CASE);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!who) return;
    setError(null);
    try {
      const created = await create.mutateAsync({
        ...d,
        contactId: who.id,
        conversationId: conversationId ?? null,
        title: d.title.trim(),
      });
      dialog.current?.close();
      if (conversationId) toast(`Caso ${caseNumber(created.number)} abierto`);
      else navigate({ to: "/postventa/$caseId", params: { caseId: created.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir el caso.");
    }
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="case-new-title" className="case-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="case-new-title">Nuevo caso de postventa</h3>
        {who ? (
          <div className="case-who">
            <Avatar name={who.name} fallback={who.waId} />
            <span className="entity-main">
              <span className="entity-name">{who.name ?? "Sin nombre"}</span>
              <span className="entity-meta mono">{formatPhone(who.waId)}</span>
            </span>
            {!contact && (
              <button className="btn btn-sm" type="button" onClick={() => setWho(null)}>
                Cambiar
              </button>
            )}
          </div>
        ) : (
          <ContactPicker onPick={setWho} />
        )}
        <CaseFields value={d} onChange={setD} idPrefix="case-new" />
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={create.isPending || !who || !d.title.trim()}>
            {create.isPending ? "Abriendo…" : "Abrir caso"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Finds the customer by name or phone (cases and part requests opened from a list). */
export function ContactPicker({ onPick, idPrefix = "case-new" }: { onPick: (c: Picked) => void; idPrefix?: string }) {
  const [q, setQ] = useState("");
  const deferred = useDeferredValue(q.trim());
  const found = useContacts(deferred);
  const items = deferred.length >= 2 ? (found.data?.items.slice(0, 6) ?? []) : [];
  return (
    <div className="field">
      <label htmlFor={`${idPrefix}-contact`}>Cliente</label>
      <input
        className="input"
        id={`${idPrefix}-contact`}
        type="search"
        placeholder="Busca por nombre o teléfono"
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {items.length > 0 && (
        <ul className="entity-list flush case-pick">
          {items.map((c) => (
            <li key={c.id}>
              <button className="entity-row" type="button" onClick={() => onPick(c)}>
                <Avatar name={c.name} fallback={c.waId} />
                <span className="entity-main">
                  <span className="entity-name">{c.name ?? "Sin nombre"}</span>
                  <span className="entity-meta mono">{formatPhone(c.waId)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {deferred.length >= 2 && found.data && items.length === 0 && (
        <span className="faint">Nadie coincide. Los clientes se agregan solos cuando escriben por WhatsApp.</span>
      )}
    </div>
  );
}
